import React from 'react';
import { observer } from 'mobx-react';
import { ListStore } from 'shared/stores/ListStore';
import { formatTimeAgo } from 'shared/utils';
import { jobPositionStore } from 'ats/pages/job-positions/JobPositionStore';
import Dropdown from 'ats/components/dropdown/Dropdown';
import { dialogStore } from 'components/layout/modals/DialogStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import JobPositionProfile from 'ats/pages/job-positions/JobPositionProfile';
import { JobVacancyStore } from 'ats/pages/job-positions/JobVacancyStore';
import JobVacanciesTable from 'ats/pages/job-positions/JobVacanciesTable';

const EXPAND_DURATION_MS = 280;

@observer
export class JobPositionCard extends React.Component {
	state = {
		isExpanded: false,
		contentMounted: false,
		contentExpanded: false
	};

	contentRef = React.createRef();
	unmountTimerRef = null;
	expandFrameRef = null;

	constructor(props) {
		super(props);
		this.jobVacancyStore = new JobVacancyStore();
		this.listStore = new ListStore(this.jobVacancyStore, {
			entityName: 'Job Vacancy'
		});
	}

	preConfigureQuery() {
		this.listStore.store.resetQuery();
		this.listStore.store.setQuery('jobId', this.props.jobPosition.id);
		this.listStore.store.setQuery('sort', {
			column: 'createdAt',
			asc: false
		});
	}

	componentDidMount() {
		this.preConfigureQuery();
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevState.isExpanded !== this.state.isExpanded) {
			this.syncExpandAnimation(this.state.isExpanded);

			if (this.state.isExpanded) {
				this.listStore.search();
			}
		}

		if (prevProps.jobPosition !== this.props.jobPosition) {
			this.preConfigureQuery();
			this.listStore.search();
		}
	}

	componentWillUnmount() {
		clearTimeout(this.unmountTimerRef);
		cancelAnimationFrame(this.expandFrameRef);
	}

	syncExpandAnimation = isExpanded => {
		clearTimeout(this.unmountTimerRef);
		cancelAnimationFrame(this.expandFrameRef);

		if (isExpanded) {
			this.setState({ contentMounted: true }, () => {
				this.expandFrameRef = requestAnimationFrame(() => {
					this.expandFrameRef = requestAnimationFrame(() => {
						this.setState({ contentExpanded: true });
					});
				});
			});
			return;
		}

		this.setState({ contentExpanded: false });
		this.unmountTimerRef = setTimeout(() => {
			this.setState({ contentMounted: false });
		}, EXPAND_DURATION_MS);
	};

	handleToggleExpanded = () => {
		this.setState({ isExpanded: !this.state.isExpanded });
	};

	handleContentTransitionEnd = event => {
		if (event.target !== this.contentRef.current) return;
		if (event.propertyName !== 'grid-template-rows') return;

		if (this.state.contentExpanded) return;

		clearTimeout(this.unmountTimerRef);
		this.setState({ contentMounted: false });
	};

	handlePaginationChange = value => {
		this.listStore.handlePaginationChange(
			value,
			this.listStore.store.query.pagination.limit
		);
	};

	handleJobPositionStatusEdit = (jobPosition, status) => {
		dialogStore.openDialog(
			{
				title: 'Update Job Position Status',
				message: 'The Job Position Status will be changed. Are you sure?',
				confirmLabel: 'Confirm',
				confirmButtonClass: 'is-primary'
			},
			() =>
				jobPositionStore
					.update(jobPosition.id, { job: { status } })
					.then(result => {
						if (result.status === 'success') {
							notificationStore.pushSuccessNotification(
								'Job Position Status updated',
								'The Job Position Status has been successfully updated.'
							);
							this.props.onSuccess && this.props.onSuccess();
							this.listStore.search();
						} else {
							notificationStore.pushErrorNotification(
								'Update Failed',
								'Failed to update the Job Position Status'
							);
						}
					})
		);
	};

	handleJobPositionTitleClick = () => {
		drawerStore.openDrawer(
			{
				containerId: ['applicants', 'pipeline', 'job-positions', 'clients'],
				showHeader: false,
				drawerId: drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE,
				props: {
					jobPositionId: this.props.jobPosition.id,
					onSuccess: () => {
						this.props.onSuccess && this.props.onSuccess();
						this.listStore.search();
					},
					onClose: () => drawerStore.closeDrawer()
				}
			},
			JobPositionProfile
		);
	};

	render() {
		const statusData = jobPositionStore.statusOptions.find(
			status => status.value === this.props.jobPosition.status
		);

		return (
			<div
				className={`job-position-card${
					this.state.isExpanded ? ' is-expanded' : ''
				}`}>
				<div className="job-position-card-header">
					<div className="job-position-card-header-left">
						<div className="job-position-card-job-title">
							<h3 onClick={this.handleJobPositionTitleClick}>
								<span className="job-position-id">
									#{this.props.jobPosition.id}
								</span>
								{this.props.jobPosition.title}
							</h3>
						</div>
					</div>
					<div className="job-position-card-details">
						<div className="status-tag">
							<Dropdown
								value={{
									...statusData,
									label: (
										<span
											className="job-status"
											style={{
												color: statusData?.colors.text
											}}>
											{statusData?.text}
										</span>
									)
								}}
								name="status"
								menuClassName="status-dropdown-menu"
								onChange={value => {
									this.handleJobPositionStatusEdit(
										this.props.jobPosition,
										value.value
									);
								}}
								hideSelectedOption
								options={jobPositionStore.statusOptions.map(status => {
									return {
										...status,
										label: (
											<span
												className="job-status"
												style={{
													color: status?.colors.text
												}}>
												{status?.text}
											</span>
										)
									};
								})}
							/>
						</div>
						<div className="date">
							{formatTimeAgo(new Date(this.props.jobPosition.updatedAt), {
								moreThanOneDay: true
							})}
						</div>
						<div className="toggle-button">
							<button onClick={this.handleToggleExpanded}>
								<img
									src="/ats/icons/chevron-down-icon.svg"
									alt="Toggle"
									className={this.state.isExpanded ? 'is-rotated' : undefined}
								/>
							</button>
						</div>
					</div>
				</div>
				{this.state.contentMounted && (
					<div
						ref={this.contentRef}
						className={`job-position-vacancies-wrapper${
							this.state.contentExpanded ? ' is-expanded' : ''
						}`}
						onTransitionEnd={this.handleContentTransitionEnd}>
						<div className="job-position-vacancies-inner">
							<div className="job-position-vacancies">
								{this.listStore.data.length > 0 && (
									<JobVacanciesTable
										data={this.listStore.data}
										sort={this.listStore.store.query.sort}
										onSort={this.listStore.handleSort}
										onSuccess={() => {
											this.listStore.search();
										}}
									/>
								)}
							</div>
						</div>
					</div>
				)}
			</div>
		);
	}
}
